// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import { type TaskTimeEntry, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { AgGridReact } from 'ag-grid-react';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { useFacilityRequired } from 'components/Mantine/GlobalFilters/FacilityRequired';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { convertTimeFrameDataToRequestParams } from 'components/Mantine/TimeFramePicker/helpers';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { useUser } from 'hooks/user/useUser';
import { getTimeTrackingColDef } from 'modules/Shop/TimeTracking/columnDefs';
import { CreateTimeEntryModal } from 'modules/Shop/TimeTracking/CreateTimeEntryModal';
import { DeleteTimeEntryModal } from 'modules/Shop/TimeTracking/DeleteTimeEntryModal';

export const TimeTrackingTable = () => {
  const { user } = useUser();
  const { selectedFacility } = useFacilityRequired();
  const { projectIds, timeFrame } = useGlobalFilters({
    projects: true,
    timeFrame: {
      defaultTimeFrame: 'This Week',
    },
  });
  const {
    fetchPage: fetchPageEntries,
    abortController: abortEntries,
    setDefaultOpts: setDefaultOptsEntries,
  } = useEvolvePaginatedGet(`shop/task/timeEntry`);

  useEffect(() => {
    setDefaultOptsEntries({
      defaultConfig: {
        params: {
          facilityId: selectedFacility.facilityId,
          projectIds,
          ...convertTimeFrameDataToRequestParams(timeFrame),
        },
      },
    });
  }, [selectedFacility.facilityId, projectIds, setDefaultOptsEntries, timeFrame]);

  const [deletingEntry, setDeletingEntry] = useState<TaskTimeEntry>();

  const colDef = useMemo(
    () =>
      getTimeTrackingColDef({
        facilityId: selectedFacility.facilityId,
        onDeleteEntry: setDeletingEntry,
      }),
    [selectedFacility.facilityId],
  );

  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'task-time-entries',
    tableViewType: TABLE_VIEW_TYPE.TIME_TRACKING,
    exportFileName: 'Time Tracking',
    fetchPage: fetchPageEntries,
    abortController: abortEntries,
    rowId: 'taskTimeEntryId',
    colDef,
  });

  return (
    <>
      <BasePageHeader
        title="Time Tracking"
        topRightComponent={
          <CreateTimeEntryModal
            defaultValue={{
              user,
            }}
            facilityId={selectedFacility.facilityId}
            onTimeEntrySaved={() => refreshGrid(gridApi)}
          />
        }
      />
      <AgGridContainer>
        {(applyWrap) => <AgGridReact {...agGridProps} onFirstDataRendered={applyWrap} />}
      </AgGridContainer>
      <DeleteTimeEntryModal
        deletingEntry={deletingEntry}
        onClose={() => setDeletingEntry(undefined)}
        refresh={() => refreshGrid(gridApi)}
      />
    </>
  );
};
